import { ref, toValue, type MaybeRef } from "vue";
import { adminV1OrganizationsProfilesList } from "@/api/admin-profiles/admin-profiles";
import type { AdminProfile } from "@/api/model";

/**
 * The other profiles in this organization that use the same email.
 *
 * One email can only ever belong to one User (User.email is unique) and a user
 * has at most one profile per organization, so "several profiles on one email"
 * means the email's owner plus the profiles linked to their AssociatedContact:
 * managed children with no login of their own, shared-login players, payer-only
 * links. The profiles list already answers exactly that — its search filter
 * matches both `user__email` and `associated_contacts__user__email`, so the
 * whole family comes back whichever side you ask from.
 */
export function useSharedEmailProfiles(orgId: MaybeRef<number | null>) {
  const profiles = ref<AdminProfile[]>([]);
  const isLoading = ref(false);

  async function fetchProfiles(email: string, excludeProfileId: number) {
    const organizationId = toValue(orgId);
    if (!organizationId || !email) {
      profiles.value = [];
      return;
    }

    isLoading.value = true;
    try {
      const response = await adminV1OrganizationsProfilesList(organizationId, {
        search: email,
        limit: 50,
        is_active: true,
      });
      profiles.value = (response?.results || []).filter(
        (profile) =>
          profile.id !== excludeProfileId &&
          // search is a fuzzy icontains across name/email/team as well, so keep
          // only rows that really are on this email.
          (profile.email === email || profile.shared_email === email)
      );
    } catch (error) {
      // A failed lookup hides the "shared account" line; it must never blank
      // the detail page around it.
      console.error("Failed to load profiles sharing this email:", error);
      profiles.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  return { profiles, isLoading, fetchProfiles };
}
